import { apiGet, apiPost, apiPatch, apiDelete } from '@/utils/api';
import type { EntryRealignment } from './scheduleService';

export interface WorkingHoursConfig {
  id: string;
  tenantId: string;
  campusId?: string;
  academicYearId?: string;
  regularStartTime: string;
  regularEndTime: string;
  ramadanStartTime?: string;
  ramadanEndTime?: string;
  ramadanStartDate?: string;
  ramadanEndDate?: string;
  ramadanAutoUpdate: boolean;
  breakStartTime?: string;
  breakDuration?: number;
  isActive: boolean;
}

export interface RamadanDates {
  startDate: string;
  endDate: string;
  hijriYear: number;
  gregorianYear: number;
  isApproximate: boolean;
}

export interface CreateWorkingHoursDto {
  campusId?: string;
  academicYearId?: string;
  regularStartTime: string;
  regularEndTime: string;
  ramadanStartTime?: string;
  ramadanEndTime?: string;
  ramadanStartDate?: string;
  ramadanEndDate?: string;
  ramadanAutoUpdate?: boolean;
  breakStartTime?: string;
  breakDuration?: number;
}

export interface SessionType {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  category: 'FORMATION_CONTINUE' | 'FORMATION_INITIALE';
  durationMinutes: number;
  frequencyPerWeek: number;
  includesBreak: boolean;
  breakDuration?: number;
  ramadanBehavior: 'KEEP_SAME' | 'SHORTEN';
  ramadanDurationMinutes?: number;
  /** Minutes paid to the professor per session (null = real duration) */
  paidDurationMinutes?: number | null;
  /** Paid minutes for a Ramadan-shortened session (null = proportional) */
  ramadanPaidDurationMinutes?: number | null;
  color?: string;
  isActive: boolean;
  sortOrder: number;
}



export interface CreateSessionTypeDto {
  name: string;
  code: string;
  category: 'FORMATION_CONTINUE' | 'FORMATION_INITIALE';
  durationMinutes: number;
  frequencyPerWeek?: number;
  includesBreak?: boolean;
  breakDuration?: number;
  ramadanBehavior?: 'KEEP_SAME' | 'SHORTEN';
  ramadanDurationMinutes?: number;
  paidDurationMinutes?: number | null;
  ramadanPaidDurationMinutes?: number | null;
  color?: string;
  sortOrder?: number;
}

export interface AcademicYearTemplate {
  id: string;
  tenantId: string;
  campusId?: string;
  startDay: number;
  startMonth: number;
  endDay: number;
  endMonth: number;
  semester1StartDay?: number;
  semester1StartMonth?: number;
  semester1EndDay?: number;
  semester1EndMonth?: number;
  semester2StartDay?: number;
  semester2StartMonth?: number;
  semester2EndDay?: number;
  semester2EndMonth?: number;
  isActive: boolean;
}

export interface CreateAcademicYearTemplateDto {
  campusId?: string;
  startDay: number;
  startMonth: number;
  endDay: number;
  endMonth: number;
  semester1StartDay?: number;
  semester1StartMonth?: number;
  semester1EndDay?: number;
  semester1EndMonth?: number;
  semester2StartDay?: number;
  semester2StartMonth?: number;
  semester2EndDay?: number;
  semester2EndMonth?: number;
}

class CalendarSettingsService {
  // Working Hours
  async getWorkingHours(campusId?: string): Promise<WorkingHoursConfig | null> {
    try {
      const params = campusId ? `?campusId=${campusId}` : '';
      const data = await apiGet<WorkingHoursConfig | WorkingHoursConfig[]>(
        `/calendar-settings/working-hours${params}`
      );
      // Backend returns an array, we want the first config (or null if empty)
      return Array.isArray(data) ? (data[0] || null) : data;
    } catch (error) {
      // Return null for 404 errors
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async createOrUpdateWorkingHours(data: CreateWorkingHoursDto, existingId?: string): Promise<WorkingHoursConfig> {
    if (existingId) {
      return apiPatch<WorkingHoursConfig>(`/calendar-settings/working-hours/${existingId}`, data);
    } else {
      return apiPost<WorkingHoursConfig>('/calendar-settings/working-hours', data);
    }
  }

  // Ramadan Dates
  async getUpcomingRamadanDates(): Promise<RamadanDates | null> {
    try {
      const data = await apiGet<RamadanDates | { message: string }>('/calendar-settings/ramadan/upcoming');
      if ('message' in data) return null; // Error response
      return data;
    } catch {
      return null;
    }
  }

  async getRamadanDatesForYear(year: number): Promise<RamadanDates | null> {
    try {
      const data = await apiGet<RamadanDates | { message: string }>(`/calendar-settings/ramadan/${year}`);
      if ('message' in data) return null; // Error response
      return data;
    } catch {
      return null;
    }
  }

  async forceUpdateRamadanDates(): Promise<{ updatedCount: number }> {
    return apiPost<{ updatedCount: number }>('/calendar-settings/ramadan/force-update');
  }

  // Session Types
  async getSessionTypes(): Promise<SessionType[]> {
    return apiGet<SessionType[]>('/calendar-settings/session-types');
  }

  async createSessionType(data: CreateSessionTypeDto): Promise<SessionType> {
    return apiPost<SessionType>('/calendar-settings/session-types', data);
  }

  async updateSessionType(
    id: string,
    data: Partial<CreateSessionTypeDto>,
  ): Promise<SessionType & { realignment?: EntryRealignment[] }> {
    return apiPatch<SessionType & { realignment?: EntryRealignment[] }>(`/calendar-settings/session-types/${id}`, data);
  }

  async deleteSessionType(id: string): Promise<void> {
    return apiDelete<void>(`/calendar-settings/session-types/${id}`);
  }

  async reorderSessionTypes(orderedIds: string[]): Promise<void> {
    return apiPatch<void>('/calendar-settings/session-types/reorder', { orderedIds });
  }

  // Academic Year Template
  async getAcademicYearTemplate(campusId?: string): Promise<AcademicYearTemplate | null> {
    try {
      const params = campusId ? `?campusId=${campusId}` : '';
      const data = await apiGet<AcademicYearTemplate | AcademicYearTemplate[]>(
        `/calendar-settings/academic-year-template${params}`
      );
      console.log('getAcademicYearTemplate response:', data);
      // Backend may return an array, we want the first item (or null if empty)
      return Array.isArray(data) ? (data[0] || null) : data;
    } catch (error) {
      // Return null for 404 errors
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async createOrUpdateAcademicYearTemplate(data: CreateAcademicYearTemplateDto, existingId?: string): Promise<AcademicYearTemplate> {
    if (existingId) {
      return apiPatch<AcademicYearTemplate>(`/calendar-settings/academic-year-template/${existingId}`, data);
    } else {
      return apiPost<AcademicYearTemplate>('/calendar-settings/academic-year-template', data);
    }
  }
}

export const calendarSettingsService = new CalendarSettingsService();
